import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'
import { fetchAllPages } from '../components/business-rules/rule-families'

// Types
export interface ComplianceControl {
  uid: string
  code: string
  domain: string
  name: Record<string, string>
  description: Record<string, string>
  enabled: boolean
  position: number
  created_at: string
  updated_at: string
}

/**
 * Kind of obligation a rule is (migration 299): a legal obligation of the
 * building, lot or site; a periodic verification report or visit of equipment;
 * or a sweep management threshold. The asset Compliance tab reads the first,
 * the Equipment tab the second.
 */
export type ComplianceRuleClass = 'regulatory' | 'periodic_inspection' | 'management'

export interface ComplianceRule {
  uid: string
  compliance_control_uid: string
  code?: string
  jurisdiction: string
  label: Record<string, string>
  description: Record<string, string>
  asset_type_uid?: string
  conditions: Record<string, unknown>
  check: Record<string, unknown>
  severity: number
  frequency_months?: number
  evidence_description?: string
  source_ref?: string
  source_url?: string
  auto_create_issue: boolean
  enabled: boolean
  /** regulatory, periodic_inspection or management (migration 299). */
  rule_class?: ComplianceRuleClass
  /** Rule family (spec 2026-10-27-18): one norm, a default and per-type overrides. */
  family?: string | null
  evidence_mode?: 'auto' | 'document'
  warn_window_days?: number | null
  // Structured scheduling/lifecycle fields the API returns (spec 2026-07-20-01):
  // exposed here so the Règles de gestion page can read the full condition, not
  // just the flat frequency_months.
  periodicity?: Record<string, unknown> | null
  temporal_scope?: Record<string, unknown> | null
  remediation?: Record<string, unknown> | null
  kind_resolved?: string
  satisfied_by_document_types?: string[] | null
  /** Universes the rule serves; empty = every universe (spec 2026-10-27-21). */
  universes?: string[]
  /** shipped (catalogue) or organization (created here, code ORG_…). */
  origin?: 'shipped' | 'organization'
  /** The organization's changes to a shipped rule, per field. */
  tuning?: Record<string, { shipped: unknown; value: unknown; by?: number | null; at?: string }>
  /** Buildings or sites an organization family member governs. */
  scope_asset_uids?: string[]
  proposed_at?: string | null
  compliance_control?: ComplianceControl
  created_at: string
  updated_at: string
}

/** Fields only a write sends (spec 2026-10-27-21). */
export interface ComplianceRuleWrite extends Partial<ComplianceRule> {
  recurrence_months?: number
  clone_of?: string
}

interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
}

/**
 * A justificatif document linked to a compliance rule×asset pair.
 * See spec 2026-06-20-04-compliance-evidence-document-linking and
 * 2026-07-05-02-compliance-evidence-validity-and-status-derivation (the four
 * validity fields + effective_valid_until, matching api/realassets-api.yaml's
 * ComplianceEvidenceItem schema and the generated front/src/api/schema.ts).
 */
export interface ComplianceEvidenceItem {
  uid: string
  document_uid: string
  document_name: string
  document_type?: string
  expires_at?: string
  issued_at?: string | null
  valid_until?: string | null
  note: string
  source_details?: Record<string, unknown> | null
  effective_valid_until?: string | null
  expired: boolean
  linked_at: string
  /**
   * Derived by the engine from a document whose type the rule declares in
   * satisfied_by_document_types, rather than hand-linked. There is no link
   * row behind it, so it cannot be patched or detached — `uid` carries the
   * document's own uid.
   */
  auto?: boolean
  /**
   * Matches the rule's declared types but carries no date the engine can
   * anchor a periodic obligation on. A lead, not a proof — it never moves
   * the status.
   */
  candidate?: boolean
}

// Valid domains for compliance controls
export const COMPLIANCE_DOMAINS = [
  'FIRE_SAFETY',
  'ELECTRICAL',
  'WATER',
  'STRUCTURAL',
  'ENVIRONMENTAL',
  'ACCESSIBILITY',
  'WORKPLACE',
  'INSURANCE',
  'LEGAL',
  'NAT_TECH_RISKS',
  'HEALTH_SAFETY',
] as const

export type ComplianceDomain = typeof COMPLIANCE_DOMAINS[number]

// Human-readable domain labels
export const DOMAIN_LABELS: Record<string, string> = {
  FIRE_SAFETY: 'Fire Safety',
  ELECTRICAL: 'Electricity',
  WATER: 'Water',
  STRUCTURAL: 'Structural',
  ENVIRONMENTAL: 'Environmental',
  ACCESSIBILITY: 'Accessibility',
  WORKPLACE: 'Workplace',
  INSURANCE: 'Insurance',
  LEGAL: 'Legal',
  NAT_TECH_RISKS: 'Natural & technological risks',
  HEALTH_SAFETY: 'Health & Safety',
}

// Severity labels and colors
export const SEVERITY_CONFIG: Record<number, { label: string; color: string }> = {
  1: { label: 'Critical', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  2: { label: 'High', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  3: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  4: { label: 'Low', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  5: { label: 'Lowest', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' },
}

/**
 * Resolved temporal kind values returned by the backend on every rule.
 * Maps to the spec 04 grouping: active obligations (recurring +
 * one_shot_active), installation conformity (at_install), historical
 * regulations (one_shot_past).
 */
export type RuleKindResolved =
  | 'recurring'
  | 'one_shot_active'
  | 'one_shot_past'
  | 'at_install'

export const KIND_RESOLVED_CONFIG: Record<RuleKindResolved, { i18nKey: string; color: string }> = {
  recurring:       { i18nKey: 'rules.kind_recurring',       color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  one_shot_active: { i18nKey: 'rules.kind_one_shot_active', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
  one_shot_past:   { i18nKey: 'rules.kind_one_shot_past',   color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  at_install:      { i18nKey: 'rules.kind_at_install',      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
}

/**
 * Three-way lifecycle grouping for the compliance rules list. `active`
 * shows the day-to-day obligations PMs watch; `installation` and
 * `historical` are collapsed-by-default audit-trail buckets.
 */
export type RuleLifecycleGroup = 'active' | 'installation' | 'historical' | 'all'

export function lifecycleGroupOf(kind: RuleKindResolved | undefined | null): Exclude<RuleLifecycleGroup, 'all'> {
  if (kind === 'at_install') return 'installation'
  if (kind === 'one_shot_past') return 'historical'
  return 'active'
}

// Query keys
export const complianceKeys = {
  controls: (org: string) => ['compliance-controls', org] as const,
  control: (org: string, uid: string) => ['compliance-control', org, uid] as const,
  rules: (org: string) => ['compliance-rules', org] as const,
  rule: (org: string, uid: string) => ['compliance-rule', org, uid] as const,
}

// Helper to get auth token
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw { status: res.status, ...body }
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// Controls hooks
export function useComplianceControls(org: string) {
  return useQuery({
    queryKey: complianceKeys.controls(org),
    queryFn: () => apiFetch<PaginatedResponse<ComplianceControl>>(
      `/api/v1/organizations/${org}/compliance/controls?limit=100`
    ),
    enabled: !!org,
    select: (data) => data.data,
  })
}

export function useComplianceControl(org: string, uid: string) {
  return useQuery({
    queryKey: complianceKeys.control(org, uid),
    queryFn: () => apiFetch<ComplianceControl>(
      `/api/v1/organizations/${org}/compliance/controls/${uid}`
    ),
    enabled: !!org && !!uid,
  })
}

export function useCreateComplianceControl(org: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<ComplianceControl>) =>
      apiFetch<ComplianceControl>(`/api/v1/organizations/${org}/compliance/controls`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.controls(org) })
    },
  })
}

export function useUpdateComplianceControl(org: string, uid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<ComplianceControl>) =>
      apiFetch<ComplianceControl>(`/api/v1/organizations/${org}/compliance/controls/${uid}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.controls(org) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.control(org, uid) })
    },
  })
}

export function useDeleteComplianceControl(org: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) =>
      apiFetch<void>(`/api/v1/organizations/${org}/compliance/controls/${uid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.controls(org) })
    },
  })
}

// Rules hooks
// Pages until `total` (spec 2026-10-27-18): the server caps a page at 100 and
// an organization is seeded with more rules than that, so a single page left
// the severity-4 ones off the Règles de gestion page.
export function useComplianceRules(org: string, controlUid?: string) {
  return useQuery({
    queryKey: [...complianceKeys.rules(org), controlUid] as const,
    queryFn: () => fetchAllPages((page, limit) => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) })
      if (controlUid) params.set('control_uid', controlUid)
      return apiFetch<PaginatedResponse<ComplianceRule>>(
        `/api/v1/organizations/${org}/compliance/rules?${params}`
      )
    }),
    enabled: !!org,
  })
}

export function useComplianceRule(org: string, uid: string) {
  return useQuery({
    queryKey: complianceKeys.rule(org, uid),
    queryFn: () => apiFetch<ComplianceRule>(
      `/api/v1/organizations/${org}/compliance/rules/${uid}`
    ),
    enabled: !!org && !!uid,
  })
}

export function useCreateComplianceRule(org: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ComplianceRuleWrite) =>
      apiFetch<ComplianceRule>(`/api/v1/organizations/${org}/compliance/rules`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.rules(org) })
    },
  })
}

export function useUpdateComplianceRule(org: string, uid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ComplianceRuleWrite) =>
      apiFetch<ComplianceRule>(`/api/v1/organizations/${org}/compliance/rules/${uid}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.rules(org) })
      queryClient.invalidateQueries({ queryKey: complianceKeys.rule(org, uid) })
    },
  })
}

export function useDeleteComplianceRule(org: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) =>
      apiFetch<void>(`/api/v1/organizations/${org}/compliance/rules/${uid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.rules(org) })
    },
  })
}

/** Restores a shipped rule's tuned fields (all of them when none is named). */
export function useResetRuleTuning(org: string, uid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fields?: string[]) =>
      apiFetch<ComplianceRule>(`/api/v1/organizations/${org}/compliance/rules/${uid}/reset-tuning`, {
        method: 'POST',
        body: JSON.stringify(fields ? { fields } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.rules(org) })
    },
  })
}

/** Proposes an organization rule for the shared catalogue. */
export function useProposeRule(org: string, uid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<ComplianceRule>(`/api/v1/organizations/${org}/compliance/rules/${uid}/propose`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: complianceKeys.rules(org) })
    },
  })
}

export interface RulePreview {
  evaluated: number
  applicable: number
  non_compliant: number
  compliant: number
  unknown: number
  truncated: boolean
  sweep_produced: boolean
}

/** Evaluates a draft rule without writing anything ("Tester"). */
export function usePreviewRule(org: string) {
  return useMutation({
    mutationFn: (body: ComplianceRuleWrite) =>
      apiFetch<RulePreview>(`/api/v1/organizations/${org}/compliance/rules/preview`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  })
}

export interface RuleDraftStatus {
  job_uid: string
  status: 'pending' | 'done' | 'failed'
  draft?: Record<string, unknown>
  compiled?: Record<string, unknown>
  error?: string
}

/** Queues a model draft of a rule described by a sentence. */
export function useRequestRuleDraft(org: string) {
  return useMutation({
    mutationFn: (body: { prompt: string; locale?: string }) =>
      apiFetch<{ job_uid: string; status: string }>(`/api/v1/organizations/${org}/compliance/rules/draft`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  })
}

/** Polls a draft until the job answered. */
export function useRuleDraft(org: string, jobUid: string | null) {
  return useQuery({
    queryKey: ['compliance-rule-draft', org, jobUid] as const,
    queryFn: () => apiFetch<RuleDraftStatus>(`/api/v1/organizations/${org}/compliance/rules/drafts/${jobUid}`),
    enabled: !!org && !!jobUid,
    refetchInterval: (q) => (q.state.data?.status === 'pending' || !q.state.data ? 1500 : false),
  })
}

// --- Evidence (justificatif) hooks ---

/**
 * List the justificatif documents linked to a rule for a given asset. Used by
 * the attach dialog; the evaluations list already embeds evidence inline.
 */
export function useRuleEvidence(org: string, assetUid: string, ruleUid: string, enabled = true) {
  return useQuery({
    queryKey: ['compliance-evidence', org, assetUid, ruleUid] as const,
    queryFn: () => apiFetch<{ data: ComplianceEvidenceItem[] }>(
      `/api/v1/organizations/${org}/assets/${assetUid}/compliance/rules/${ruleUid}/evidence`
    ),
    enabled: !!org && !!assetUid && !!ruleUid && enabled,
    select: (d) => d.data,
  })
}

/** Optional validity metadata shared by link (POST) and patch (PATCH) requests. */
export interface EvidenceValidityInput {
  issued_at?: string | null
  valid_until?: string | null
  note?: string
  source_details?: Record<string, unknown> | null
}

/**
 * Invalidates the asset's properties query (carries the `compliance_status`
 * aggregate badge shown on the Data tab) after an evidence mutation, using
 * the predicate pattern documented in front/CLAUDE.md — matching method +
 * path + path params, not a naive queryKey array equality check, since the
 * auto-generated openapi-react-query key embeds params in an object.
 */
function invalidateAssetProperties(queryClient: ReturnType<typeof useQueryClient>, org: string, assetUid: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1]
        if (path === '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties') {
          const params = queryKey.find((item) => item && typeof item === 'object' && 'params' in item)?.params
          if (params?.path?.orgCode === org && params?.path?.assetUid === assetUid) {
            return true
          }
        }
      }
      return false
    },
  })
}

/**
 * Link a document as evidence for a rule on an asset. Invalidates the
 * evaluations list (which embeds evidence), the per-rule evidence list, and
 * the asset's properties (compliance_status aggregate) — the backend
 * re-derives the pair's status synchronously in the same request (spec
 * 2026-07-05-02 §7), so a valid proof flips its row to green without reload.
 */
export function useLinkEvidence(org: string, assetUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ruleUid, documentUid, issued_at, valid_until, note, source_details }: {
      ruleUid: string
      documentUid: string
    } & EvidenceValidityInput) =>
      apiFetch<ComplianceEvidenceItem>(
        `/api/v1/organizations/${org}/assets/${assetUid}/compliance/rules/${ruleUid}/evidence`,
        { method: 'POST', body: JSON.stringify({ document_uid: documentUid, issued_at, valid_until, note, source_details }) }
      ),
    onSuccess: (_data, { ruleUid }) => {
      // Prefix-invalidate the org: a proof attached from a rolled-up site row
      // changes a *building's* verdict, and the site's own list is keyed on the
      // site uid, so keying on assetUid alone would leave it stale.
      queryClient.invalidateQueries({ queryKey: ['compliance-evaluations', org] })
      queryClient.invalidateQueries({ queryKey: ['compliance-inspections', org] })
      queryClient.invalidateQueries({ queryKey: ['compliance-evidence', org, assetUid, ruleUid] })
      invalidateAssetProperties(queryClient, org, assetUid)
    },
  })
}

/**
 * Update an existing evidence link's validity metadata (issued_at/valid_until/
 * note/source_details). The linked document itself is immutable through this
 * endpoint — detach and re-link to change it. Same re-derivation +
 * invalidation story as useLinkEvidence.
 */
export function usePatchEvidence(org: string, assetUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ruleUid, evidenceUid, issued_at, valid_until, note, source_details }: {
      ruleUid: string
      evidenceUid: string
    } & EvidenceValidityInput) =>
      apiFetch<ComplianceEvidenceItem>(
        `/api/v1/organizations/${org}/assets/${assetUid}/compliance/rules/${ruleUid}/evidence/${evidenceUid}`,
        { method: 'PATCH', body: JSON.stringify({ issued_at, valid_until, note, source_details }) }
      ),
    onSuccess: (_data, { ruleUid }) => {
      // Prefix-invalidate the org: a proof attached from a rolled-up site row
      // changes a *building's* verdict, and the site's own list is keyed on the
      // site uid, so keying on assetUid alone would leave it stale.
      queryClient.invalidateQueries({ queryKey: ['compliance-evaluations', org] })
      queryClient.invalidateQueries({ queryKey: ['compliance-inspections', org] })
      queryClient.invalidateQueries({ queryKey: ['compliance-evidence', org, assetUid, ruleUid] })
      invalidateAssetProperties(queryClient, org, assetUid)
    },
  })
}

/**
 * Unlink an evidence document from a rule on an asset.
 */
export function useUnlinkEvidence(org: string, assetUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ruleUid, evidenceUid }: { ruleUid: string; evidenceUid: string }) =>
      apiFetch<void>(
        `/api/v1/organizations/${org}/assets/${assetUid}/compliance/rules/${ruleUid}/evidence/${evidenceUid}`,
        { method: 'DELETE' }
      ),
    onSuccess: (_data, { ruleUid }) => {
      // Prefix-invalidate the org: a proof attached from a rolled-up site row
      // changes a *building's* verdict, and the site's own list is keyed on the
      // site uid, so keying on assetUid alone would leave it stale.
      queryClient.invalidateQueries({ queryKey: ['compliance-evaluations', org] })
      queryClient.invalidateQueries({ queryKey: ['compliance-inspections', org] })
      queryClient.invalidateQueries({ queryKey: ['compliance-evidence', org, assetUid, ruleUid] })
      invalidateAssetProperties(queryClient, org, assetUid)
    },
  })
}

/**
 * One periodic inspection (a rule of class `periodic_inspection`) on one
 * asset, as served by `GET /assets/{uid}/compliance/inspections`: the latest
 * evaluation flattened to its last date, next due date, status and report.
 */
export interface AssetInspection {
  asset_uid: string
  asset_name?: string
  evaluation_uid: string
  rule_uid: string
  rule_code?: string
  label: Record<string, string>
  source_ref?: string
  status: 'COMPLIANT' | 'NON_COMPLIANT' | 'EXPIRING' | 'UNKNOWN' | 'ERROR'
  last_inspection_date?: string
  next_due_date?: string
  evaluated_at: string
  evidence: ComplianceEvidenceItem[]
}

/**
 * Periodic inspections of an asset — and, with `includeDescendants`, of every
 * asset under it, which is how the Equipment tab fills its Vérifications cells
 * with one request per root.
 */
export function useAssetInspections(org: string, assetUid: string | undefined, includeDescendants: boolean) {
  return useQuery({
    queryKey: ['compliance-inspections', org, assetUid, includeDescendants],
    queryFn: () => apiFetch<{ data: AssetInspection[] }>(
      `/api/v1/organizations/${org}/assets/${assetUid}/compliance/inspections${includeDescendants ? '?include_descendants=true' : ''}`
    ),
    enabled: !!org && !!assetUid,
    select: (d) => d.data ?? [],
  })
}
